/**
 * Write side of the per-feature Heidi Labs opt-ins.
 *
 * Kept separate from `labs-overrides.ts` (the read-side leaf) on purpose:
 * `useMutateUpdateUser` pulls in the store / scribeAPI module graph, which
 * transitively reaches `@/features/flags`. Since the flag hooks import the
 * read-side leaf, co-locating the write hook there would form an import cycle
 * (flags → labs-overrides → useMutateUpdateUser → … → flags). Only the settings
 * UI imports this module, so the heavy graph never loads on the flag-read path.
 */
import { useMutateUpdateUser } from '@/hooks/mutation/useMutateUpdateUser';
import { useCallback } from 'react';

import {
  useLabsOverrides,
  type LabsFeatureId,
  type LabsOverrideMap,
} from './labs-overrides';

/**
 * Merge-writes a single feature's opt-in to the backend. Sends the COMPLETE
 * override map (Option A full-object replace) on every toggle — clearing a key
 * is done by omitting it. Optimistic via `useMutateUpdateUser`, so the switch
 * flips instantly; `isPending` disables the control while the write is in flight.
 *
 * NOTE: this only ever writes an explicit `true`/`false`; there is no path back
 * to the absent ("follow the flag") tri-state. A boolean Switch can't express
 * three states, so toggling OFF persists `false`, it does NOT unset the key. If
 * a "reset to default" affordance is ever needed, add a `clearOptIn(id)` that
 * omits the key from the map rather than assuming toggling off unsets it.
 */
interface UseSetLabsFeatureOptInOptions {
  /**
   * Mutation-level callback for persisted overrides. It survives an initiating
   * component unmounting while the request is in flight.
   */
  onOptInPersisted?: (overrides: LabsOverrideMap) => void;
}

export const useSetLabsFeatureOptIn = (
  hookOptions?: UseSetLabsFeatureOptInOptions
): {
  setOptIn: (
    id: LabsFeatureId,
    value: boolean,
    options?: { onSuccess?: () => void }
  ) => void;
  isPending: boolean;
} => {
  const { mutate, isPending } = useMutateUpdateUser({
    onUpdateUserSuccess: (variables) => {
      const overrides = variables.session_settings?.labs_feature_overrides;
      if (overrides) hookOptions?.onOptInPersisted?.(overrides);
    },
  });
  const current = useLabsOverrides();
  const setOptIn = useCallback(
    (id: LabsFeatureId, value: boolean, options?: { onSuccess?: () => void }) => {
      mutate(
        { session_settings: { labs_feature_overrides: { ...current, [id]: value } } },
        // Only confirm once the write actually persisted, so a failed mutation
        // doesn't surface a misleading "enabled"/"disabled" toast.
        { onSuccess: options?.onSuccess }
      );
    },
    [mutate, current]
  );
  return { setOptIn, isPending };
};
